import type { StacAsset } from 'src/shared/state/api-slices/stacCatalogueEngineSlice';

/**
 * Splitting an asset into the fields the form knows and the rest.
 *
 * STAC assets are open documents: producers attach extension fields the engine
 * stores verbatim and sometimes reads (`proj:code` decides whether a tile archive
 * can be served at all). A form with fixed inputs would drop those on the first
 * edit, so the two halves are separated here and rejoined on save — the editor
 * renders inputs for one half and a free-form editor for the other.
 *
 */
export const STANDARD_ASSET_FIELDS = ['href', 'type', 'title', 'roles'] as const;

export type StandardAssetField = typeof STANDARD_ASSET_FIELDS[number];

export type StandardAssetFields = Partial<Pick<StacAsset, StandardAssetField>>;

const isStandard = (field: string): field is StandardAssetField => (STANDARD_ASSET_FIELDS as readonly string[]).includes(field);

/** Everything the form has no input for. Values are kept as they are, objects included. */
export const customFieldsOf = (asset: StacAsset): Record<string, unknown> =>
    Object.fromEntries(Object.entries(asset).filter(([field]) => !isStandard(field)));

/** A field the asset does not state stays absent, rather than becoming an empty string. */
export const standardFieldsOf = (asset: StacAsset): StandardAssetFields =>
    Object.fromEntries(Object.entries(asset).filter(([field]) => isStandard(field))) as StandardAssetFields;

/**
 * Rejoins the halves. The standard fields win: the free-form editor must not be a
 * way to set `href` behind the form's back, where validation and the `s3://` rule live.
 */
export const assembleAsset = (standard: StandardAssetFields, custom: Record<string, unknown>): StacAsset => {
    const customWithoutStandard = Object.fromEntries(Object.entries(custom).filter(([field]) => !isStandard(field)));
    // Standard fields first. STAC does not care about key order, but a reader does — `href` and
    // `type` are what an asset *is*, and leading with an extension field made the raw document
    // read oddly. Nothing collides: the standard names are filtered out of `custom` above.
    return { ...standard, ...customWithoutStandard } as StacAsset;
};

/** The one tile source media type the engine can read (`selection.rs`). */
const TILE_SOURCE_MEDIA_TYPE = 'application/vnd.pmtiles';

/**
 * Whether the engine will treat this asset as a source of tiles — and so demand a
 * `proj:code`. Read from the declared media type, never the href: a `.pmtiles`
 * suffix declares nothing, and the engine takes the same position.
 */
export const isTileSource = (asset: StacAsset): boolean => {
    const declared = asset.type?.trim().split(';')[0].trim().toLowerCase();
    return declared === TILE_SOURCE_MEDIA_TYPE;
};
